import type { JsonObject } from "./json";
import type { BillingMetric } from "./pricing";
import type { UsageFilterOption } from "./usage-filter-options";
import { USAGE_QUERY_ENDPOINTS } from "./usage-query-reference";

export const ADMIN_REFRESH_HEADER = "x-admin-refresh";

/**
 * Frozen usage-reporting semantics (admin-console-status TODO-01).
 *
 * Time: the population axis is `gateway.requests.received_at` and every
 * window is the half-open interval `[start, end)`. The database stores UTC
 * instants; API callers pass ISO-8601 instants and may pass a
 * `timeZoneOffsetMinutes` offset (east positive, -720..840) that only affects
 * day/month/… bucket edges, never stored data or window matching.
 *
 * Sources: a request may carry several usage records (unique per source).
 * Reporting reads exactly one per request with the fixed priority
 * `reconciled` > `provider` > `estimated`; `reconciled` is the final truth
 * after correction, `provider` is vendor evidence, `estimated` is a fallback.
 *
 * Normalization status (derived at query time, there is no stored column):
 * - `normalized`: the selected record has a valid non-empty v1 component array.
 * - `partial`: reserved for "some raw vendor fields could not be mapped into
 *   any standard metric"; the current pipeline cannot detect it, so queries
 *   never emit it yet but callers may already filter for it.
 * - `invalid`: a record exists but the normalized envelope is unusable
 *   (missing/non-array/empty components).
 * - `missing`: no usage record exists for the request.
 *
 * Rating status (derived from the highest-priority charge of the request):
 * - `billed`: a succeeded or refunded charge exists.
 * - `billing_failed`: the chosen charge failed.
 * - `unsettled`: usage exists, no charge yet, and a published sale price
 *   version effective at usage time exists (or the request has not
 *   succeeded, so settlement is not expected yet).
 * - `unpriced`: usage exists, no charge, request succeeded, and no published
 *   sale price version was effective at usage time for the model/operation.
 * - `usage_missing`: the request's outcome already succeeded, so usage evidence
 *   was expected, yet no usage record exists — a real anomaly worth investigating.
 * - `usage_not_expected`: the request never produced a successful outcome
 *   (rejected, failed, cancelled, or still running), so the absence of usage
 *   is the normal result, not a billing anomaly.
 *
 * "不适用 / 0 / 缺失" stay distinct: a metric absent from `metrics` means
 * nothing was metered (missing or not applicable); a present `0` value was
 * genuinely metered as zero. `tokens.applicable = false` means the model has
 * no token rate in any published sale/cost price version, so empty token
 * metrics for such models are "not applicable", never "missing".
 */

export const USAGE_SOURCES = ["provider", "estimated", "reconciled"] as const;
export type UsageSource = (typeof USAGE_SOURCES)[number];

export const USAGE_NORMALIZATION_STATUSES = [
  "normalized",
  "partial",
  "invalid",
  "missing",
] as const;
export type UsageNormalizationStatus = (typeof USAGE_NORMALIZATION_STATUSES)[number];

export const USAGE_RATING_STATUSES = [
  "billed",
  "unsettled",
  "billing_failed",
  "unpriced",
  "usage_missing",
  "usage_not_expected",
] as const;
export type UsageRatingStatus = (typeof USAGE_RATING_STATUSES)[number];

export const USAGE_GROUP_BYS = [
  "minute",
  "hour",
  "day",
  "month",
  "user",
  "apiKey",
  "provider",
  "channel",
  "model",
  "operation",
] as const;
export type UsageGroupBy = (typeof USAGE_GROUP_BYS)[number];

export const USAGE_AMOUNT_KINDS = ["sale", "cost", "gross"] as const;
export type UsageAmountKind = (typeof USAGE_AMOUNT_KINDS)[number];

export const TOKEN_METRICS = [
  "input_tokens",
  "cached_input_tokens",
  "cache_write_tokens",
  "cache_creation_5m_input_tokens",
  "cache_creation_1h_input_tokens",
  "cache_read_input_tokens",
  "output_tokens",
] as const satisfies readonly BillingMetric[];

/** Decimal string with up to 8 fraction digits; only `gross` may be negative. */
export const MONEY_STRING_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d{1,8})?$/;
export type MoneyString = string;

export interface UsageTimeWindow {
  /** Inclusive ISO-8601 instant. */
  start: string;
  /** Exclusive ISO-8601 instant; must be after `start`. */
  end: string;
}

export const USAGE_PRODUCT_IDS = ["vibepaper", "starcut", "starcat", "unattributed"] as const;

export interface UsageFilters extends UsageTimeWindow {
  productIds?: string[];
  timeZoneOffsetMinutes?: number;
  userIds?: string[];
  userQuery?: string;
  apiKeyIds?: string[];
  providerIds?: string[];
  channelIds?: string[];
  modelIds?: string[];
  operations?: string[];
  upstreamModelId?: string;
  requestStatuses?: string[];
  usageSources?: UsageSource[];
  normalizationStatuses?: UsageNormalizationStatus[];
  ratingStatuses?: UsageRatingStatus[];
}

export interface UsageSummaryQuery {
  filters: UsageFilters;
  /** At most one time granularity; keys are ordered as given. */
  groupBy: UsageGroupBy[];
  limit: number;
  cursor?: string;
}

export interface UsageSummaryGroup {
  /** Bucket start as a UTC instant; present for time granularities. */
  time?: string;
  userId?: string | null;
  apiKeyId?: string | null;
  providerId?: string | null;
  channelId?: string | null;
  modelId?: string | null;
  operation?: string | null;
}

export interface UsageSummaryAmount {
  kind: UsageAmountKind;
  currency: string;
  /** Decimal string; sums of numeric(20,8) charge amounts. */
  amount: MoneyString;
}

export interface UsageSummaryRow {
  group: UsageSummaryGroup;
  requests: {
    total: number;
    /** Final successful calls; asynchronous tasks must also finish required delivery. */
    succeeded: number;
    /** Final failed, cancelled, rejected and delivery_failed calls. */
    failed: number;
  };
  usage: {
    /** Requests whose prioritized usage record exists. */
    evidence: number;
    /** Requests without any usage record; equals rating.usage_missing + rating.usage_not_expected. */
    missing: number;
    sources: Record<UsageSource, number>;
    normalization: Record<Exclude<UsageNormalizationStatus, "missing">, number>;
  };
  rating: Record<UsageRatingStatus, number>;
  costReporting: {
    /** CNY revenue from succeeded charges with a matched CNY reporting cost. */
    coveredSale?: MoneyString | null;
    /** Succeeded charges with a CNY reporting cost. */
    reported: number;
    /** Succeeded charges whose procurement cost was not recorded. */
    missingCost: number;
    /** Succeeded charges with an original cost but no matching frozen FX rate. */
    missingRate: number;
  };
  tokens: {
    /** Null unless `model` is a group key. */
    applicable: boolean | null;
    requestsWithTokenMetrics: number;
  };
  /** Metric quantities summed across dimensions; absent keys were never metered. */
  metrics: Partial<Record<BillingMetric, number>>;
  amounts: UsageSummaryAmount[];
  /** Operating amounts use the CNY reporting currency. Amount sums include
   *  only `succeeded` charges; missing costs/rates never become zero. */
}

export interface UsageSummaryPage {
  data: UsageSummaryRow[];
  /** Present when more groups follow; opaque, valid only for the same query. */
  nextCursor: string | null;
}

export interface UsageOverviewPage {
  products?: Array<{ id: string; requests: number; succeeded?: number; failed?: number; evidence?: number; missing?: number }>;
  models: UsageSummaryRow[];
  channels?: UsageSummaryRow[];
  channelNames?: UsageFilterOption[];
  trend: UsageSummaryPage;
  daily?: UsageSummaryPage;
  anomalies: UsageAnomalySummaryPage;
  modelNames: UsageFilterOption[];
}

export const FINANCE_CHART_DIMENSIONS = ["product", "kind", "model", "channel"] as const;
export type FinanceChartDimension = typeof FINANCE_CHART_DIMENSIONS[number];

export interface FinanceChartRow {
  id: string;
  label: string | null;
  /** Catalog family for model-series filtering; null for other dimensions. */
  family: string | null;
  modelName?: string | null;
  modelKind?: string | null;
  providerId?: string | null;
  providerName?: string | null;
  settledRequests: number;
  sale: MoneyString | null;
  /** CNY revenue from the same cost-covered records used for gross profit. */
  coveredSale?: MoneyString | null;
  /** Recorded CNY costs only; absence is never a measured zero. */
  cost: MoneyString | null;
  /** CNY gross profit from records with matched revenue and reporting cost. */
  gross: MoneyString | null;
  missingCost: number;
  missingRate: number;
}

export interface FinanceChartsPage {
  data: Record<FinanceChartDimension, FinanceChartRow[]>;
}

export interface FinanceTableExportInput {
  dimension: FinanceChartDimension | "day";
  title: string;
  scope: string;
  note: string;
  headers: string[];
  /** Decimal amounts remain strings to preserve billing precision in Excel. */
  rows: Array<Array<string | number | null>>;
}

export function validateFinanceTableExport(value: unknown): asserts value is FinanceTableExportInput {
  if (!isObject(value) || typeof value.dimension !== "string" || !["day", ...FINANCE_CHART_DIMENSIONS].includes(value.dimension)) throw new Error("Invalid table dimension");
  for (const [key, limit] of [["title", 200], ["scope", 500], ["note", 2000]] as const) {
    if (typeof value[key] !== "string" || value[key].length > limit) throw new Error(`Invalid table ${key}`);
  }
  if (!Array.isArray(value.headers) || !value.headers.length || value.headers.length > 32 || value.headers.some(header => typeof header !== "string" || !header || header.length > 200)) throw new Error("Invalid table headers");
  const columns = value.headers.length;
  if (!Array.isArray(value.rows) || value.rows.length > 50_000 || value.rows.some(row => !Array.isArray(row) || row.length !== columns || row.some(cell => cell !== null && !(typeof cell === "string" && cell.length <= 10_000) && !(typeof cell === "number" && Number.isFinite(cell))))) throw new Error("Invalid table rows");
}

export const USAGE_FILTER_SELECTION_LIMIT = 200;

export function validateUsageFilters(value: unknown): asserts value is UsageFilters {
  if (!isObject(value)) throw new Error("Usage filters must be an object");
  const filters = value;
  if (filters.productIds !== undefined && (!isStringArray(filters.productIds)
    || filters.productIds.length > USAGE_PRODUCT_IDS.length
    || filters.productIds.some(id => !(USAGE_PRODUCT_IDS as readonly string[]).includes(id)))) {
    throw new Error("filters.productIds must contain supported product IDs");
  }
  const start = Date.parse(String(filters.start));
  const end = Date.parse(String(filters.end));
  if (!Number.isFinite(start)) throw new Error("filters.start must be an ISO-8601 instant");
  if (!Number.isFinite(end)) throw new Error("filters.end must be an ISO-8601 instant");
  if (start >= end) throw new Error("filters.end must be after filters.start ([start, end) window)");
  if (filters.timeZoneOffsetMinutes !== undefined && (
    !Number.isSafeInteger(filters.timeZoneOffsetMinutes)
    || (filters.timeZoneOffsetMinutes as number) < -720
    || (filters.timeZoneOffsetMinutes as number) > 840
  )) {
    throw new Error("filters.timeZoneOffsetMinutes must be an integer between -720 and 840");
  }
  for (const key of ["userIds", "apiKeyIds", "providerIds", "channelIds", "modelIds", "operations", "requestStatuses"] as const) {
    const list = filters[key];
    if (list !== undefined && (!isStringArray(list) || list.length > USAGE_FILTER_SELECTION_LIMIT)) {
      throw new Error(`filters.${key} must be at most ${USAGE_FILTER_SELECTION_LIMIT} non-empty strings`);
    }
  }
  if (filters.userQuery !== undefined && (typeof filters.userQuery !== "string" || !filters.userQuery.trim())) {
    throw new Error("filters.userQuery must be a non-empty string");
  }
  if (filters.upstreamModelId !== undefined
    && (typeof filters.upstreamModelId !== "string" || filters.upstreamModelId === "")) {
    throw new Error("filters.upstreamModelId must be a non-empty string");
  }
  for (const key of ["usageSources", "normalizationStatuses", "ratingStatuses"] as const) {
    const list = filters[key];
    if (list === undefined) continue;
    if (!isStringArray(list)) throw new Error(`filters.${key} must be an array of non-empty strings`);
    const allowed = key === "usageSources"
      ? USAGE_SOURCES
      : key === "normalizationStatuses"
        ? USAGE_NORMALIZATION_STATUSES
        : USAGE_RATING_STATUSES;
    if (list.some((item) => !(allowed as readonly string[]).includes(item))) {
      throw new Error(`filters.${key} contains an unsupported status`);
    }
  }
}

export function validateUsageSummaryQuery(
  value: unknown,
): asserts value is UsageSummaryQuery {
  if (!isObject(value)) throw new Error("Usage summary query must be an object");
  validateUsageFilters(value.filters);
  if (!Array.isArray(value.groupBy) || value.groupBy.length === 0) {
    throw new Error("groupBy must be a non-empty array");
  }
  const timeGranularities = value.groupBy.filter((key) =>
    ["minute", "hour", "day", "month"].includes(String(key))
  );
  if (timeGranularities.length > 1) {
    throw new Error("groupBy accepts at most one time granularity");
  }
  const seen = new Set<string>();
  for (const key of value.groupBy) {
    if (!(USAGE_GROUP_BYS as readonly string[]).includes(String(key))) {
      throw new Error(`groupBy contains an unsupported key: ${String(key)}`);
    }
    if (seen.has(String(key))) throw new Error("groupBy contains duplicate keys");
    seen.add(String(key));
  }
  if (!Number.isSafeInteger(value.limit) || (value.limit as number) < 1 || (value.limit as number) > USAGE_QUERY_ENDPOINTS.summary.maxLimit) {
    throw new Error(`limit must be an integer between 1 and ${USAGE_QUERY_ENDPOINTS.summary.maxLimit}`);
  }
  if (value.cursor !== undefined && (typeof value.cursor !== "string" || value.cursor === "")) {
    throw new Error("cursor must be a non-empty string");
  }
}

const CURSOR_PREFIX = "v1.offset.";

/** Cursors are opaque offsets; they are only valid for the identical query. */
export function encodeUsageSummaryCursor(offset: number): string {
  return `${CURSOR_PREFIX}${offset}`;
}

export function parseUsageSummaryCursor(value: string): number | null {
  if (!value.startsWith(CURSOR_PREFIX)) return null;
  const offset = Number(value.slice(CURSOR_PREFIX.length));
  return Number.isSafeInteger(offset) && offset >= 0 ? offset : null;
}

const KEYSET_CURSOR_PREFIX = "v1.key.";

/**
 * Keyset cursor over `(received_at, request id)` in descending order, shared
 * by the details and anomaly queries.
 */
export function encodeUsageKeysetCursor(receivedAt: string, requestId: string): string {
  return `${KEYSET_CURSOR_PREFIX}${encodeURIComponent(receivedAt)}|${encodeURIComponent(requestId)}`;
}

export function parseUsageKeysetCursor(
  value: string,
): { receivedAt: string; requestId: string } | null {
  if (!value.startsWith(KEYSET_CURSOR_PREFIX)) return null;
  const parts = value.slice(KEYSET_CURSOR_PREFIX.length).split("|");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  let receivedAt: string;
  let requestId: string;
  try {
    receivedAt = decodeURIComponent(parts[0]);
    requestId = decodeURIComponent(parts[1]);
  } catch {
    return null;
  }
  // The request id is bound as a UUID; rejecting foreign shapes here keeps
  // malformed cursors a 400 instead of a cast error downstream.
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestId)) {
    return null;
  }
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/.test(receivedAt) || !Number.isFinite(Date.parse(receivedAt))) return null;
  // Date.parse 会把 2 月 30 日或 24:00 归一化；不能据此接受数据库会拒绝的游标。
  if (new Date(receivedAt).toISOString().slice(0, 19) !== receivedAt.slice(0, 19)) return null;
  return { receivedAt, requestId };
}

// ---- Details (TODO-05) ----

export interface UsageDetailsQuery {
  filters: UsageFilters;
  /** Page size, 1..200. */
  limit: number;
  cursor?: string;
}

export interface UsageDetailUsage {
  id: string | null;
  source: UsageSource | null;
  occurredAt: string | null;
  /** Vendor-native evidence; request/response bodies and credentials are excluded. */
  rawUsage: JsonObject;
  normalizedUsage: JsonObject;
  normalizationStatus: UsageNormalizationStatus;
}

export interface UsageDetailCharge {
  id: string;
  status: string;
  saleAmount: MoneyString | null;
  saleCurrency: string | null;
  costAmount: MoneyString | null;
  costCurrency: string | null;
  costReportingAmount: MoneyString | null;
  costReportingCurrency: string | null;
  costReportingFxRate: MoneyString | null;
  costReportingFxSource: string | null;
  costReportingFxEffectiveAt: string | null;
  /** Price revision numbers bound at settlement time. */
  discountBps: number | null;
  /** Settlement-time price evaluation snapshot, including its `lines` and `issues`. */
  breakdown: JsonObject;
}

export const USAGE_INPUT_FEATURES = ["text", "image", "video", "audio", "first_frame", "last_frame", "reference_image", "reference_video", "reference_audio"] as const;
export type UsageInputFeature = typeof USAGE_INPUT_FEATURES[number];

export interface UsageDetailRow {
  /** Database request id; the drill-down key. */
  requestId: string;
  /** Client-facing request key (`gateway.requests.request_id`). */
  requestKey: string;
  receivedAt: string;
  status: string;
  httpStatus: number | null;
  durationMs: number | null;
  errorCode: string | null;
  userId: string | null;
  userEmail: string | null;
  apiKeyId: string | null;
  modelId: string;
  modelPublicId: string | null;
  /** Explicit Run mode, when this request belongs to a Run. */
  inputMode: string | null;
  /** Input evidence observed on this request, never the model's capability list. */
  inputFeatures: UsageInputFeature[];
  requestedModel: string | null;
  operation: string | null;
  publicProtocol: string | null;
  providerId: string | null;
  channelId: string | null;
  upstreamModelId: string | null;
  usage: UsageDetailUsage;
  ratingStatus: UsageRatingStatus;
  charge: UsageDetailCharge | null;
}

export interface UsageDetailsPage {
  data: UsageDetailRow[];
  nextCursor: string | null;
}

export function validateUsageDetailsQuery(
  value: unknown,
): asserts value is UsageDetailsQuery {
  if (!isObject(value)) throw new Error("Usage details query must be an object");
  validateUsageFilters(value.filters);
  if (!Number.isSafeInteger(value.limit) || (value.limit as number) < 1 || (value.limit as number) > USAGE_QUERY_ENDPOINTS.details.maxLimit) {
    throw new Error(`limit must be an integer between 1 and ${USAGE_QUERY_ENDPOINTS.details.maxLimit}`);
  }
  if (value.cursor !== undefined && (typeof value.cursor !== "string" || value.cursor === "")) {
    throw new Error("cursor must be a non-empty string");
  }
}

// ---- Billing anomalies (TODO-06) ----

/**
 * Mutually exclusive anomaly buckets; every request lands in at most one,
 * first match wins (settlement_failed > normalization_failed > unpriced >
 * usage_missing > dimension_mismatch). Note that `usage_missing` only covers
 * succeeded requests — failed requests without usage never expected billing —
 * so its count can differ from the summary's `rating.usage_missing`, which
 * has no request-status restriction.
 */
export const USAGE_ANOMALY_CATEGORIES = [
  "usage_missing",
  "normalization_failed",
  "unpriced",
  "dimension_mismatch",
  "settlement_failed",
] as const;
export type UsageAnomalyCategory = (typeof USAGE_ANOMALY_CATEGORIES)[number];

export interface UsageAnomaliesQuery {
  filters: UsageFilters;
  categories?: UsageAnomalyCategory[];
  /** Page size, 1..200. */
  limit: number;
  cursor?: string;
}

export interface UsageAnomalyRow {
  category: UsageAnomalyCategory;
  /** Database request id; drill down via the details query. */
  requestId: string;
  requestKey: string;
  receivedAt: string;
  userId: string | null;
  modelId: string;
  modelPublicId: string | null;
  providerId: string | null;
  channelId: string | null;
  upstreamModelId: string | null;
  /** Cause codes, e.g. charge error code or evaluation issue codes. */
  reason: string;
  /**
   * True when the money impact is quantifiable from stored charge amounts
   * (settlement_failed, dimension_mismatch, and billed normalization_failed
   * cases carry a sale amount).
   */
  amountKnown: boolean;
}

export interface UsageAnomaliesPage {
  data: UsageAnomalyRow[];
  nextCursor: string | null;
}

export interface UsageAnomalySummaryQuery {
  filters: UsageFilters;
  /** Optional per-category breakdowns; each adds one grouped aggregate. */
  breakdowns?: Array<"provider" | "channel" | "model">;
}

export interface UsageAnomalySummaryRow {
  category: UsageAnomalyCategory;
  count: number;
  firstOccurredAt: string;
  lastOccurredAt: string;
}

export interface UsageAnomalyBreakdownRow {
  category: UsageAnomalyCategory;
  dimension: "provider" | "channel" | "model";
  dimensionId: string | null;
  count: number;
}

export interface UsageAnomalySummaryPage {
  categories: UsageAnomalySummaryRow[];
  breakdowns: UsageAnomalyBreakdownRow[];
}

export function validateUsageAnomaliesQuery(
  value: unknown,
): asserts value is UsageAnomaliesQuery {
  if (!isObject(value)) throw new Error("Usage anomalies query must be an object");
  validateUsageFilters(value.filters);
  if (value.categories !== undefined) {
    if (!isStringArray(value.categories)
      || (value.categories as string[]).some((item) => !(USAGE_ANOMALY_CATEGORIES as readonly string[]).includes(item))) {
      throw new Error("categories contains an unsupported anomaly category");
    }
  }
  if (!Number.isSafeInteger(value.limit) || (value.limit as number) < 1 || (value.limit as number) > 200) {
    throw new Error("limit must be an integer between 1 and 200");
  }
  if (value.cursor !== undefined && (typeof value.cursor !== "string" || value.cursor === "")) {
    throw new Error("cursor must be a non-empty string");
  }
}

export function validateUsageAnomalySummaryQuery(
  value: unknown,
): asserts value is UsageAnomalySummaryQuery {
  if (!isObject(value)) throw new Error("Usage anomaly summary query must be an object");
  validateUsageFilters(value.filters);
  if (value.breakdowns !== undefined) {
    if (!Array.isArray(value.breakdowns)
      || value.breakdowns.length > 2
      || value.breakdowns.some((item) => !["provider", "channel", "model"].includes(String(item)))) {
      throw new Error("breakdowns accepts at most two of provider, channel, model");
    }
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value)
    && value.length > 0
    && value.every((item) => typeof item === "string" && item !== "");
}
